import { QueryKeys } from "@app/constants/query-keys.ts";
import { useApis } from "@app/data/apis.ts";
import {
    AddEditRosterDetailPostModel,
    InsertAssignmentRosterStaffPostModel,
    UpdateRosterStaffActivationPostModel,
    UpdateRosterStaffProjectRolePostModel,
    UpdateRosterStaffSortByPostModel,
} from "@app/data/engine.ts";
import { handleApiError } from "@app/helpers/error-helper.tsx";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useQuery } from "bi-lib";
import { toast } from "react-toastify";

interface Props {
    assignmentId?: number;
    staffId?: number;
}

export default function useRosterData({ assignmentId, staffId }: Props = {}) {
    const apis = useApis();
    const queryClient = useQueryClient();

    const projectRoleIdQuery = useQuery({
        queryKey: [QueryKeys.RosterProjectRole, { assignmentId, staffId }],
        queryFn: () => apis.engine.roster.rosterProjectRoleIdList({ AssignmentId: assignmentId, StaffId: staffId }),
        enabled: assignmentId != undefined && staffId != undefined,
    });

    const saveRosterDetailMutation = useMutation({
        mutationFn: (post: AddEditRosterDetailPostModel) => apis.engine.roster.rosterSaveRosterDetailCreate(post),
        onSuccess: (_, { rosterDetailId }) => {
            void queryClient.invalidateQueries({ queryKey: [QueryKeys.AssignmentRosterDetailList] });
            void queryClient.invalidateQueries({
                queryKey: [QueryKeys.AssignmentRosterDetail, { rosterDetailId }],
            });

            toast.success("Roster detail updated");
        },
        onError: (error: Error) => handleApiError(error, "Failed to update roster detail"),
    });

    const updateRosterStaffProjectRoleMutation = useMutation({
        mutationFn: (post: UpdateRosterStaffProjectRolePostModel) =>
            apis.engine.roster.rosterUpdateRosterStaffProjectRoleCreate(post),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: [QueryKeys.AssignmentRosterList] });
            void queryClient.invalidateQueries({ queryKey: [QueryKeys.RosterProjectRole] });

            toast.success("Roster role updated");
        },
        onError: (error: Error) => handleApiError(error, "Failed to update roster role"),
    });

    const updateRosterStaffActivationMutation = useMutation({
        mutationFn: (post: UpdateRosterStaffActivationPostModel) =>
            apis.engine.roster.rosterUpdateRosterStaffActivationCreate(post),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: [QueryKeys.AssignmentRosterList] });

            toast.success("Roster activation updated");
        },
        onError: (error: Error) => handleApiError(error, "Failed to update roster"),
    });

    const updateRosterStaffSortByMutation = useMutation({
        mutationFn: (post: UpdateRosterStaffSortByPostModel) =>
            apis.engine.roster.rosterUpdateRosterStaffSortByCreate(post),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: [QueryKeys.AssignmentRosterList] });

            toast.success("Roster updated");
        },
        onError: (error: Error) => handleApiError(error, "Failed to update roster"),
    });

    const insertRosterStaffMutation = useMutation({
        mutationFn: (post: InsertAssignmentRosterStaffPostModel) =>
            apis.engine.roster.rosterInsertRosterStaffCreate(post),
        onSuccess: (_, { assignmentId }) => {
            void queryClient.invalidateQueries({
                queryKey: [QueryKeys.AssignmentRosterList, { id: assignmentId }],
            });

            toast.success("Roster created");
        },
        onError: (error: Error) => handleApiError(error, "Failed to create roster"),
    });

    const deleteRosterDetailMutation = useMutation({
        mutationFn: (id: number) => apis.engine.roster.rosterDeleteRosterDetailDelete({ id }),
        onSuccess: () => {
            void queryClient.invalidateQueries({
                queryKey: [QueryKeys.AssignmentRosterList],
            });

            toast.success("Roster detail deleted");
        },
        onError: (error: Error) => handleApiError(error, "Failed to delete roster detail"),
    });

    return {
        projectRoleId: projectRoleIdQuery,
        saveRosterDetail: saveRosterDetailMutation,
        updateRosterStaffProjectRole: updateRosterStaffProjectRoleMutation,
        updateRosterStaffActivation: updateRosterStaffActivationMutation,
        updateRosterStaffSortBy: updateRosterStaffSortByMutation,
        insertRosterStaff: insertRosterStaffMutation,
        deleteRosterDetail: deleteRosterDetailMutation,
    };
}
